import { Navigate, useRoutes } from "react-router-dom";
import Login from "../../pages/login/Login";
import NotFound from "../../pages/notFound/NotFound";
import AdminUser from "../../pages/adminUser/AdminUser";
import ForgotPassword from "../../pages/forgotPassword/ForgotPassword";
import InvalidLink from "../../pages/forgotPassword/InvalidLink";
import CheckYourEmail from "../../pages/forgotPassword/CheckYourEmail";
import SetNewPassword from "../../pages/forgotPassword/SetNewPassword";
import PasswordReset from "../../pages/forgotPassword/PasswordReset";
import AppLayout from "../layout/AppLayout";
import FamilyManagement from "../../pages/familyManagement/FamilyManagement";
import Notifications from "../../pages/notifications/Notifications";
import SignUp from "../../pages/signUp/SignUp";
import VerifyEmail from "../../pages/verifyEmail/VerifyEmail";
import InstitutionSelector from "../../pages/institutionSelector/InstitutionSelector";
import MasterAdminLayout from "../../pages/masterAdmin/MasterAdminLayout";
import MasterAdminInstitutions from "../../pages/masterAdmin/pages/MasterAdminInstitutions";
import MasterAdminAdministrators from "../../pages/masterAdmin/pages/MasterAdminAdministrators";
import MasterAdminUsers from "../../pages/masterAdmin/pages/MasterAdminUsers";
import MasterAdminDeeplinks from "../../pages/masterAdmin/pages/MasterAdminDeeplinks";
import { useAuth } from "../utilities/useAuth";
import PrivateRoute from "../utilities/protectedRoute";
import LoadingSpinner from "../../component/loading/LoadingSpinner";
import MapOverview from "../../pages/mapOverview/MapOverview";
import DispatchBoard from "../../pages/dispatchBoard/DispatchBoard";
import Vehicles from "../../pages/vehicles/Vehicles";
import Drivers from "../../pages/drivers/Drivers";
import InvalidPasswordLink from "../../pages/signUp/InvalidPasswordLink";
import VerificationReceived from "../../pages/signUp/VerificationReceived";
import UnverifiedUser from "../../pages/signUp/UnverifiedUser";
import Stops from "../../pages/stops/Stops";
import Trips from "../../pages/trips/Trips";
import Scheduling from "../../pages/scheduling/Scheduling";
import StudentSchedules from "../../pages/scheduling/StudentSchedules";
import AuditLogs from "../../pages/auditLogs/AuditLogs";
import ServiceAnnouncement from "../../pages/serviceAnnouncement/ServiceAnnouncement";
import BusPolicy from "../../pages/busPolicy/BusPolicy";
import GeneralPolicy from "../../pages/generalPolicy/GeneralPolicy";
import ExternalApps from "../../pages/externalApps/ExternalApps";
import {
  DASHBOARD_PATHS,
  LEGACY_DASHBOARD_REDIRECTS,
  dashboardPath,
} from "../dashboardRoutes.js";
export default function Router() {
  const { user, loading, privilege } = useAuth();
  const modules = privilege?.modules;
  const role = privilege?.role;

  // Define the available module routes
  const moduleRoutes = [
    { key: "mapOverview", path: DASHBOARD_PATHS.mapOverview, element: <MapOverview /> },
    { key: "dispatchBoard", path: DASHBOARD_PATHS.dispatchBoard, element: <DispatchBoard /> },
    {
      key: "scheduling",
      path: DASHBOARD_PATHS.scheduling,
      element: <Scheduling />,
    },
    {
      key: "scheduling",
      path: DASHBOARD_PATHS.studentSchedules,
      element: <StudentSchedules />,
    },
    { key: "vehicles", path: DASHBOARD_PATHS.vehicles, element: <Vehicles /> },
    { key: "drivers", path: DASHBOARD_PATHS.drivers, element: <Drivers /> },
    {
      key: "routeManagement",
      path: DASHBOARD_PATHS.stops,
      element: <Stops />,
    },
    {
      key: "routeManagement",
      path: DASHBOARD_PATHS.trips,
      element: <Trips />,
    },
    { key: "administrators", path: DASHBOARD_PATHS.administrators, element: <AdminUser /> },
    {
      key: "familyManagement",
      path: DASHBOARD_PATHS.familyManagement,
      element: <FamilyManagement />,
    },
    {
      key: "campaignManagement",
      path: DASHBOARD_PATHS.notifications,
      element: <Notifications />,
    },
    {
      key: "auditLogs",
      path: DASHBOARD_PATHS.auditLogs,
      element: <AuditLogs />,
      allowedRoles: ["SUPERADMIN", "ADMIN", "EDITOR"],
    },
    {
      key: "serviceAnnouncement",
      path: DASHBOARD_PATHS.serviceAnnouncement,
      element: <ServiceAnnouncement />,
      allowedRoles: ["SUPERADMIN", "ADMIN", "EDITOR"],
    },
    {
      key: "busPolicy",
      path: DASHBOARD_PATHS.busPolicy,
      element: <BusPolicy />,
      allowedRoles: ["SUPERADMIN", "ADMIN", "EDITOR"],
    },
    {
      key: "generalPolicy",
      path: DASHBOARD_PATHS.generalPolicy,
      element: <GeneralPolicy />,
      allowedRoles: ["SUPERADMIN", "ADMIN", "EDITOR"],
    },
    {
      path: DASHBOARD_PATHS.externalApps,
      element: <ExternalApps />,
    },
  ];

  const legacyDashboardRedirects = Object.entries(
    LEGACY_DASHBOARD_REDIRECTS
  ).map(([legacyPath, canonicalSegment]) => ({
    path: legacyPath,
    element: <Navigate to={dashboardPath(canonicalSegment)} replace />,
  }));

  // Find the first enabled module
  const firstAccessibleModule = moduleRoutes.find(({ key }) => modules?.[key]);

  // Define routes before rendering
  const routes = useRoutes([
    {
      path: "/dashboard",
      element: (
        <PrivateRoute>
          <AppLayout />
        </PrivateRoute>
      ),
      children: [
        ...moduleRoutes.map(({ key, path, element, allowedRoles }) => ({
          path,
          element: key ? (
            <PrivateRoute requiredModule={key} allowedRoles={allowedRoles}>
              {element}
            </PrivateRoute>
          ) : (
            <PrivateRoute>{element}</PrivateRoute>
          ),
        })),
        ...legacyDashboardRedirects,
        {
          path: "/dashboard",
          element: firstAccessibleModule ? (
            <Navigate to={`/dashboard/${firstAccessibleModule.path}`} replace />
          ) : (
            <Navigate to="/404" replace />
          ),
        },
      ],
    },
    {
      path: "/",
      children: [
        {
          path: "/",
          element: user ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          ),
        },
        {
          path: "login",
          element: user ? <Navigate to="/dashboard" replace /> : <Login />,
        },
        { path: "signup", element: <SignUp /> },
        { path: "signup/*", element: <SignUp /> },
        { path: "verifyemail", element: <VerifyEmail /> },
        { path: "verifyemail/*", element: <VerifyEmail /> },
        { path: "institutionselector", element: <InstitutionSelector /> },
        {
          path: "master-admin",
          element: (
            <PrivateRoute>
              <MasterAdminLayout />
            </PrivateRoute>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="institutions" replace />,
            },
            {
              path: "institutions",
              element: <MasterAdminInstitutions />,
            },
            {
              path: "administrators",
              element: <MasterAdminAdministrators />,
            },
            {
              path: "users",
              element: <MasterAdminUsers />,
            },
            {
              path: "deeplinks",
              element: <MasterAdminDeeplinks />,
            },
          ],
        },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
        { path: "invalidlink", element: <InvalidPasswordLink /> },
        { path: "verificationreceived", element: <VerificationReceived /> },
        { path: "unverifieduser", element: <UnverifiedUser /> },
        { path: "forgotpassword", element: <ForgotPassword /> },
        { path: "invalidresetlink", element: <InvalidLink /> },
        { path: "emailcheck", element: <CheckYourEmail /> },
        { path: "setnewpassword", element: <SetNewPassword /> },
        { path: "setnewpassword/*", element: <SetNewPassword /> },
        { path: "confirmreset", element: <PasswordReset /> },
        { path: "parents/resetpassword/*", element: <SetNewPassword /> },
        { path: "parents/resetpassword", element: <SetNewPassword /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <LoadingSpinner size="large" color="#ffb502" />
      </div>
    );
  }

  return routes;
}
